import { useDispatch, useSelector } from 'react-redux';
import {
	useEffect, useRef, useMemo, useCallback, useState,
} from 'react';
import { GreenroomState as GS } from '../stores/types/types';
import { SUB_TABS, SUB_TABS_MAP, TABS } from '../stores/types/tabs';
import { setActiveTab, setRightDrawerOpen } from '../stores/actions/tabs';
import { setEditingTheme } from '../stores/actions/themes';
import { FeatureFlagsEnum } from '../types/blp-feature-flags';
import { selectFeatureFlag } from '../stores/selectors/blp-feature-flags';

export const useTabs = () => {
	const dispatch = useDispatch();

	const {activeTab, previousTab} = useSelector((s: GS) => s.TabsReducer);
	const isViewingTheme = useSelector((s: GS) => !!s.ThemesReducer.viewedTheme);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const selectedMedContent = useSelector((s: GS) => s.GeneratedContentReducer.selectedMediaItem);
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const epidemicSoundEnabled = useSelector(selectFeatureFlag(FeatureFlagsEnum.epidemic_sound));

	const selectedStylePackDrawer = useSelector(
		(s: GS) => s.GeneratedContentReducer.selectedStylePackDrawer,
	);

	const derenderingTabRef = useRef<string>();
	const derenderTimeoutRef = useRef<NodeJS.Timeout>();
	const [unRenderTrigger, setUnRenderTrigger] = useState(1);

	const validParentPrevTabRef = useRef(previousTab as TABS);
	
	useEffect(() => {
		// do not set the sub tabs themselves as valid fallbacks
		if (SUB_TABS.indexOf(previousTab) === -1) {
			validParentPrevTabRef.current = previousTab;
		}
	}, [previousTab]);

	const prevTabIsValid = () => !!(validParentPrevTabRef?.current !== TABS.NONE);

	useEffect(() => {
		if (
			(!selectedMedContent && !currentGraphicId && activeTab === TABS.GENERATE_MEDIA_CONTENT)
			|| (!currentGraphicId && !selectedMedContent && SUB_TABS.includes(activeTab))
		) {
			const hasValidPrevTab = prevTabIsValid();
			dispatch(setRightDrawerOpen(hasValidPrevTab));
			dispatch(setActiveTab(hasValidPrevTab ? validParentPrevTabRef.current : TABS.NONE));
		}
	}, [activeTab, selectedMedContent, currentGraphicId]);

	const checkPathClasses = (path: Array<any>) => {
		const len = path.length;
		let doKeepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				// Check for excluded classes first - they take precedence
				if (
					typeof path[i].className === 'string'
					&& path[i].className.includes('shot-thumbnail')
				) {
					doKeepOpen = false;
					break;
				}

				if (
					typeof path[i].className === 'string'
					&& (
						path[i].className.includes('graphic-wrapper')
						|| path[i].className.includes('graphic-menu-container')
						|| path[i].className.includes('graphic-drag-area')
						|| path[i].className.includes('right-drawer-button')
						|| path[i].className.includes('right-drawer-wrapper')
						|| path[i].className.includes('settings')
						|| path[i].className.includes('overlay')
						|| path[i].className.includes('tab')
						|| path[i].className.includes('header')
						|| path[i].className.includes('right-drawer-wrapper')
						|| path[i].className.includes('moveable-custom-graphic-buttons')
						|| path[i].className.includes('showboard-drawer')
						|| path[i].className.includes('header-small-button')
						|| path[i].className.includes('audio-only-feeds-wrapper')
						|| path[i].className.includes('switch-wrapper')
						|| path[i].className.includes('duration-picker')
						|| path[i].className.includes('button')
						|| path[i].className.includes('gr-picture')
						|| (
							path[i].className.includes('shot-thumbnail-inside')
							&& path[i].className.includes('selectable')
						)
					)
				) {
					doKeepOpen = true;
					break;
				}
			}
		}

		return doKeepOpen;
	};

	const checkPathIds = (path: Array<any>) => {
		let doKeepOpen = false;
		if (path.some((el: Element) => [
			'video-hover-options-portal',
			'video-hover-options-button',
			'controls-container-editing',
			'generate-content-media-editor',
			'video-wrapper'
		].includes(el.id))) {
			doKeepOpen = true;
		}
		return doKeepOpen;
	};

	useEffect(() => {
		const handleClickOff = (e: any) => {
			const path = e.composedPath();

			const doKeepOpenBecauseClass = checkPathClasses(path);
			const doKeepOpenBecauseId = checkPathIds(path);

			if (!doKeepOpenBecauseClass && !doKeepOpenBecauseId) {
				if (isViewingTheme) {
					dispatch(setEditingTheme(null, false));
				}  else if (SUB_TABS_MAP[activeTab]) {
					// only close the stylepack tab when clicked off
					dispatch(setActiveTab(TABS.NONE));
				}
			}
		};

		if (SUB_TABS.indexOf(activeTab) > -1) {
			window.removeEventListener('click', handleClickOff);
			window.addEventListener('click', handleClickOff);
		}

		return () => {
			window.removeEventListener('click', handleClickOff);
		};
	}, [activeTab, isViewingTheme]);

	const setDerenderTimeout = (oldActiveTab: string) => {
		derenderingTabRef.current = oldActiveTab;
		if (derenderTimeoutRef.current) clearTimeout(derenderTimeoutRef.current);
		derenderTimeoutRef.current = setTimeout(() => {
			derenderingTabRef.current = '';
			setUnRenderTrigger((prev) => prev + 1);
		}, 500);
	};

	const checkTabLabel = useCallback((tabLabel: string) => (
		activeTab === tabLabel
		|| derenderingTabRef.current === tabLabel
	), [activeTab, derenderingTabRef]);

	const isStrictStylePack = useCallback(() => {
		if (!currentGraphicId)
			return false;

		const graphicContent = graphics.find(x => x.id === currentGraphicId);
		if (!graphicContent?.template_type)
			return false;

		const template = contentTemplates.find(x => x.type === graphicContent.template_type);
		return template?.data?.strict || false;

	}, [currentGraphicId, graphics, contentTemplates]);

	const shouldRender = useMemo(() => ({
		feeds: checkTabLabel(TABS.FEEDS),
		shots: checkTabLabel(TABS.SHOTS),
		media: checkTabLabel(TABS.MEDIA),
		notesTeleprompter: checkTabLabel(TABS.NOTES_TELEPROMPTER),
		// chat hard-renders (for listeners maybe?)
		chat: true,
		settings: checkTabLabel(TABS.SETTINGS),
		generateMediaContent: !!(checkTabLabel(TABS.GENERATE_MEDIA_CONTENT)),
		overlays: !!(checkTabLabel(TABS.OVERLAYS) || currentGraphicId) && !isStrictStylePack(),
		audience: checkTabLabel(TABS.AUDIENCE),
		// Epidemic-sourced media is gated behind the epidemic_sound flag; don't
		// render it even if activeTab is somehow still AUDIO_SETTINGS from before
		// the flag was toggled off. Non-epidemic audio (uploads, capsule) is unaffected.
		audioSettings: checkTabLabel(TABS.AUDIO_SETTINGS)
			&& !(selectedMedContent?.provider === 'epidemic sound' && !epidemicSoundEnabled),
	}), [
		unRenderTrigger,
		checkTabLabel,
		selectedStylePackDrawer,
		currentGraphicId,
		isStrictStylePack,
		epidemicSoundEnabled,
		selectedMedContent?.provider,
	]);

	return {
		derenderingTabRef,
		prevTabRef: validParentPrevTabRef,
		derenderTimeoutRef,
		setDerenderTimeout,
		shouldRender,
	};
};

export default useTabs;
